import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import InvoiceSearch from "./InvoiceProduct/InvoiceSearch";
import InvoiceProductForm from "./InvoiceProduct/InvoiceProductForm";
import RetentionModal, { RET_IR_OPTIONS, RET_IVA_OPTIONS } from "./InvoiceRetention/Retention";
import TotalForm from "./InvoiceProduct/TotalForm";

export default function InfoProduct({
  onProductDataChange,
  rows,
  setRows,
  descripcion,
  setDescripcion,
}) {
  const searchRef = useRef();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [modalIR, setModalIR] = useState({ open: false, rowIdx: null });
  const [modalIVA, setModalIVA] = useState({ open: false, rowIdx: null });

  const safeRows = Array.isArray(rows) ? rows : [];

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  const calcRow = (r) => {
    const qty = r.cantidad === "" ? 0 : parseInt(r.cantidad, 10) || 0;
    const price = r.pvp === "" ? 0 : parseFloat(r.pvp) || 0;
    const base = price * qty;

    const mode = r.descuentoModo === "val" ? "val" : "pct";
    const pct = r.descuentoPct === "" ? 0 : parseFloat(r.descuentoPct) || 0;
    const val = r.descuentoValor === "" ? 0 : parseFloat(r.descuentoValor) || 0;

    const descTotal =
      mode === "val" ? clamp(val, 0, base) : clamp(base * (pct / 100), 0, base);

    const subtotal = base - descTotal;

    return { descTotal, subtotal, base };
  };

  const totals = safeRows.reduce(
    (acc, r) => {
      const { descTotal, subtotal } = calcRow(r);
      if (r.iva_porcentaje === 15) acc.sub15 += subtotal;
      else if (r.iva_porcentaje === 5) acc.sub5 += subtotal;
      else acc.sub0 += subtotal;
      acc.descuento += descTotal;
      return acc;
    },
    { sub15: 0, sub5: 0, sub0: 0, descuento: 0 }
  );

  const iva15 = totals.sub15 * 0.15;
  const iva5 = totals.sub5 * 0.05;
  const total = totals.sub15 + totals.sub5 + totals.sub0 + iva15 + iva5;

  const productosPayload = useMemo(() => {
    return safeRows.map((r) => ({
      codigo_principal: r.codigo_principal || "",
      codigo_auxiliar: r.codigo_auxiliar || "",
      descripcion: r.descripcion || "",
      detalles: r.detalles || "",
      descripcion_full: r.descripcion_full || "",
      cantidad: r.cantidad ?? "",
      pvp: r.pvp ?? "",
      iva_porcentaje: r.iva_porcentaje ?? 0,
      descuentoModo: r.descuentoModo || "pct",
      descuentoPct: r.descuentoPct ?? "",
      descuentoValor: r.descuentoValor ?? "",
      ret_ir: r.ret_ir || "",
      ret_iva: r.ret_iva || "",
      retenciones: [
        ...(String(r.ret_ir || "").trim()
          ? [{ codigo: "1", codigo_retencion: String(r.ret_ir).trim() }]
          : []),
        ...(String(r.ret_iva || "").trim()
          ? [{ codigo: "2", codigo_retencion: String(r.ret_iva).trim() }]
          : []),
      ],
    }));
  }, [safeRows]);

  const retencionesResumen = useMemo(() => {
    const items = safeRows.map((r, idx) => ({
      idx,
      ret_ir: r.ret_ir || "",
      ret_iva: r.ret_iva || "",
    }));
    const ir_codigos = Array.from(
      new Set(items.map((x) => x.ret_ir).filter((x) => String(x).trim() !== ""))
    );
    const iva_codigos = Array.from(
      new Set(items.map((x) => x.ret_iva).filter((x) => String(x).trim() !== ""))
    );
    return { items, ir_codigos, iva_codigos };
  }, [safeRows]);

  useEffect(() => {
    onProductDataChange?.({
      productos: productosPayload,
      productos_raw: safeRows,
      retenciones: retencionesResumen,
      subtotal15: totals.sub15,
      subtotal5: totals.sub5,
      subtotal0: totals.sub0,
      descuento_total: totals.descuento,
      iva15,
      iva5,
      ice: 0,
      total,
      descripcion: descripcion || "",
    });
  }, [
    productosPayload,
    safeRows,
    retencionesResumen,
    totals.sub15,
    totals.sub5,
    totals.sub0,
    totals.descuento,
    iva15,
    iva5,
    total,
    descripcion,
    onProductDataChange,
  ]);

  const handleAdd = ({
    pvp,
    iva_porcentaje,
    iva_valor,
    detalles,
    descuentoModo,
    descuentoPct,
    descuentoValor,
  }) => {
    if (!selectedProduct) {
      setErrorMessage("Debe seleccionar un producto.");
      return;
    }

    const baseName = (selectedProduct.name || "").split(" (")[0];
    const codePrincipal = selectedProduct.code || "";
    const codeAux = selectedProduct.codigo_auxiliar || "";
    const variantLabel =
      selectedProduct.variant_type && selectedProduct.value ? selectedProduct.value : "";
    const det = detalles || variantLabel;

    const price = parseFloat(pvp);
    if (isNaN(price)) {
      setErrorMessage("Precio inválido.");
      return;
    }

    const descripcionFull =
      `${codePrincipal || ""}${codeAux ? ` - ${codeAux}` : ""}`.trim() +
      ` - ${baseName || ""}` +
      (det ? ` - ${det}` : "");

    const modo = descuentoModo === "val" ? "val" : "pct";
    const pct = String(toNum(descuentoPct)).trim() === "" ? "0" : String(toNum(descuentoPct));
    const val =
      String(toNum(descuentoValor)).trim() === ""
        ? "0.00"
        : String(toNum(descuentoValor).toFixed(2));

    setRows([
      ...safeRows,
      {
        codigo_principal: codePrincipal,
        codigo_auxiliar: codeAux,
        cantidad: "1",
        descripcion: baseName,
        detalles: det,
        descripcion_full: descripcionFull,
        pvp: price.toFixed(2),
        iva_porcentaje,
        iva_valor,
        descuentoModo: modo,
        descuentoPct: modo === "pct" ? pct : pct,
        descuentoValor: modo === "val" ? val : val,
        ret_ir: "",
        ret_iva: "",
      },
    ]);

    setSelectedProduct(null);
    searchRef.current?.clear();
    setErrorMessage("");
  };

  const handleDelete = (idx) => {
    setRows(safeRows.filter((_, i) => i !== idx));
  };

  const setRowDiscountFromPct = (r, pctStr) => {
    const qty = parseInt(r.cantidad, 10) || 0;
    const price = parseFloat(r.pvp) || 0;
    const base = price * qty;
    const pct = toNum(pctStr);
    const val = clamp(base * (pct / 100), 0, base);
    return {
      ...r,
      descuentoModo: "pct",
      descuentoPct: pctStr,
      descuentoValor: val.toFixed(2),
    };
  };

  const setRowDiscountFromVal = (r, valStr) => {
    const qty = parseInt(r.cantidad, 10) || 0;
    const price = parseFloat(r.pvp) || 0;
    const base = price * qty;
    const val = clamp(toNum(valStr), 0, base);
    const pct = base > 0 ? (val / base) * 100 : 0;
    return {
      ...r,
      descuentoModo: "val",
      descuentoValor: valStr,
      descuentoPct: pct > 0 ? pct.toFixed(2) : "0",
    };
  };

  const recalcDiscountOnBaseChange = (r) => {
    const mode = r.descuentoModo === "val" ? "val" : "pct";
    if (mode === "val") return setRowDiscountFromVal(r, r.descuentoValor ?? "0.00");
    return setRowDiscountFromPct(r, r.descuentoPct ?? "0");
  };

  const handleFieldChange = (idx, field, value) => {
    setRows(
      safeRows.map((r, i) => {
        if (i !== idx) return r;

        if (field === "cantidad") {
          if (value === "" || /^\d*$/.test(value)) {
            const next = { ...r, cantidad: value };
            return recalcDiscountOnBaseChange(next);
          }
          return r;
        }

        if (field === "pvp") {
          const next = { ...r, pvp: value === "" ? "" : value };
          return recalcDiscountOnBaseChange(next);
        }

        if (field === "descuentoPct") {
          if (value === "" || /^\d*\.?\d*$/.test(value)) {
            return setRowDiscountFromPct(r, value === "" ? "" : value);
          }
          return r;
        }

        if (field === "descuentoValor") {
          if (value === "" || /^\d*\.?\d*$/.test(value)) {
            return setRowDiscountFromVal(r, value === "" ? "" : value);
          }
          return r;
        }

        if (field === "iva_porcentaje") return { ...r, iva_porcentaje: value };
        if (field === "ret_ir" || field === "ret_iva") return { ...r, [field]: value };

        return { ...r, [field]: value };
      })
    );
  };

  const pickRetIR = (it) => {
    if (modalIR.rowIdx === null) return;
    handleFieldChange(modalIR.rowIdx, "ret_ir", it.codigo);
    setModalIR({ open: false, rowIdx: null });
  };

  const pickRetIVA = (it) => {
    if (modalIVA.rowIdx === null) return;
    handleFieldChange(modalIVA.rowIdx, "ret_iva", it.codigo);
    setModalIVA({ open: false, rowIdx: null });
  };

  return (
    <Box sx={{ width: "100%" }}>
      {errorMessage && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <Grid container spacing={2}>
        <InvoiceSearch ref={searchRef} onSelect={setSelectedProduct} />
        <InvoiceProductForm producto={selectedProduct} onAdd={handleAdd} />
      </Grid>

      <RetentionModal
        open={modalIR.open}
        onClose={() => setModalIR({ open: false, rowIdx: null })}
        title="Seleccionar tipo de retención"
        items={RET_IR_OPTIONS}
        onPick={pickRetIR}
      />

      <RetentionModal
        open={modalIVA.open}
        onClose={() => setModalIVA({ open: false, rowIdx: null })}
        title="Seleccionar tipo de retención"
        items={RET_IVA_OPTIONS}
        onPick={pickRetIVA}
      />

      <Box sx={{ mt: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={{ color: "#fff" }}>Cant.</TableCell>
              <TableCell sx={{ color: "#fff" }}>Descripción</TableCell>
              <TableCell sx={{ color: "#fff" }}>Precio U.</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc. ($)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Ret. IR</TableCell>
              <TableCell sx={{ color: "#fff" }}>Ret. IVA</TableCell>
              <TableCell sx={{ color: "#fff" }}>Subtotal</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {safeRows.map((r, i) => {
              const { descTotal, subtotal } = calcRow(r);
              return (
                <TableRow key={i}>
                  <TableCell>
                    <TextField
                      value={r.cantidad}
                      onChange={(e) => handleFieldChange(i, "cantidad", e.target.value)}
                      sx={{ width: 60 }}
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 1 / 0 }}>
                    <TextField
                      value={r.descripcion_full || ""}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={6}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.pvp}
                      onChange={(e) => handleFieldChange(i, "pvp", e.target.value)}
                      type="number"
                      sx={{ width: 100 }}
                      inputProps={{ step: "0.01" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      value={r.iva_porcentaje}
                      onChange={(e) => handleFieldChange(i, "iva_porcentaje", e.target.value)}
                      sx={{ width: 90 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.descuentoPct ?? ""}
                      onChange={(e) => handleFieldChange(i, "descuentoPct", e.target.value)}
                      sx={{ width: 70 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>${descTotal.toFixed(2)}</TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <TextField
                        value={r.ret_ir || ""}
                        onChange={(e) => handleFieldChange(i, "ret_ir", e.target.value)}
                        sx={{ width: 80, maxWidth: 80 }}
                        inputProps={{ maxLength: 50 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setModalIR({ open: true, rowIdx: i })}
                        sx={{
                          bgcolor: "#1976d2",
                          color: "#fff",
                          borderRadius: 1,
                          "&:hover": { bgcolor: "#1565c0" },
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <TextField
                        value={r.ret_iva || ""}
                        onChange={(e) => handleFieldChange(i, "ret_iva", e.target.value)}
                        sx={{ width: 80, maxWidth: 80 }}
                        inputProps={{ maxLength: 50 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setModalIVA({ open: true, rowIdx: i })}
                        sx={{
                          bgcolor: "#1976d2",
                          color: "#fff",
                          borderRadius: 1,
                          "&:hover": { bgcolor: "#1565c0" },
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography>${subtotal.toFixed(2)}</Typography>
                      <IconButton onClick={() => handleDelete(i)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <TotalForm
        descripcion={descripcion}
        setDescripcion={setDescripcion}
        totals={totals}
        iva15={iva15}
        iva5={iva5}
        total={total}
      />
    </Box>
  );
}
